import { injectOptional } from "@/tsyringe-extend"
import deepmerge from "deepmerge"
import { defineStore } from "pinia"
import { container, injectable, instanceCachingFactory, registry, singleton } from "tsyringe"
import { type Ref, type App, ref } from "vue"
import type { Router, RouteRecordRaw } from "vue-router"
import { CORE_PLUGIN, CORE_ROUTER, CORE_ROUTER_EXTEND, RouteExtend } from "@/core"
import type { Plugin } from "../../../Plugin"
import { PLUGIN_CONSOLE_PAGE } from "../../BuiltinPluginSymbol"
import { ConsolePageOptionInjectKey, type ConsolePageOption } from "./Console"
import ConsolePageLayout from "./views/PageLayout.vue"
import SecondConsolePageLayout from "./views/PageLayoutSecond.vue"
import { useMiscStore } from "@/plugins/base"
export * from "./Console"

export const createDefaultConsolePageOption = () => {
  return {
    path: "/console",
    layout: {
      console: {
        head: {
          left: [],
          right: []
        },
        navigation: {
          items: []
        },
        left: {
          logo: undefined,
          middle: []
        },
        right: {
          title: []
        }
      },
      collapsed: false,
      cachedViews: []
    }
  }
}

export interface ConsolePageService {
  isCollapsed(): Ref<boolean>
  /**
   *  配置顶部左边需要的组件name
   */
  addHeadLeft(compName: string): void
  /**
   *配置顶部右边需要的组件name
   */
  addHeadRight(compName: string): void
  /**
   * 配置顶部右边需要的组件name，并倒序添加
   */
  addHeadRightReverse(compName: string): void
  /**
   * 配置中间内容部分需要的组件name
   */
  addNavigation(compName: string): void
  /**
   *  配置左边布局需要的logo地址name
   */
  setLeftLogo(compName: string): void
  /**
   * 配置左边布局需要的组件name
   */
  addLeftMiddle(compName: string): void
  /**
   * 添加子路由
   */
  addChildRoute(route: RouteRecordRaw): void
  /**
   * 添加根路由
   */
  getChildRoute(): RouteRecordRaw[]
  /**
   * 添加需要keepAlive的页面
   */
  addKeepAlivePage(compName: string): void
}

/**
 *  统一处理所有涉及国际化的切换
 */
export class DefaultConsolePageService implements ConsolePageService {
  isCollapsed(): Ref<boolean> {
    const store = useConsolePageLayout()
    return ref(store.$state.layout.collapsed || false)
  }

  children: RouteRecordRaw[] | any = [];

  addHeadLeft(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.head.left.push(compName)
  }

  addHeadRight(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.head.right.push(compName)
  }

  addHeadRightReverse(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.head.right.unshift(compName)
  }

  addNavigation(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.navigation.items.push(compName)
  }

  setLeftLogo(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.left.logo = compName
  }

  addLeftMiddle(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.left.middle.push(compName)
  }

  addRightTitle(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.console.right.title.push(compName)
  }

  addChildRoute(route: RouteRecordRaw): void {
    const routeExtend = container.resolve<RouteExtend>(CORE_ROUTER_EXTEND)
    route.beforeEnter = [routeExtend.authRouter]
    this.children.push(route)
  }

  addKeepAlivePage(compName: string): void {
    const store = useConsolePageLayout()
    store.$state.layout.cachedViews.push(compName)
  }

  getChildRoute(): RouteRecordRaw[] {
    return this.children
  }
}

export const useConsolePageLayout: any = defineStore(PLUGIN_CONSOLE_PAGE.toString(), {
  state: () => createDefaultConsolePageOption()
})

@singleton()
@registry([
  {
    token: CORE_PLUGIN,
    useFactory: instanceCachingFactory<Plugin<ConsolePageOption, ConsolePageService>>((c) => c.resolve(DefaultConsolePagePlugin))
  },
  {
    token: PLUGIN_CONSOLE_PAGE,
    useFactory: instanceCachingFactory<Plugin<ConsolePageOption, ConsolePageService>>((c) => c.resolve(DefaultConsolePagePlugin))
  }
])
@injectable()
export class DefaultConsolePagePlugin implements Plugin<ConsolePageOption, ConsolePageService> {
  id: string | symbol = PLUGIN_CONSOLE_PAGE;
  options: ConsolePageOption
  service: ConsolePageService

  constructor(
    @injectOptional(ConsolePageOptionInjectKey)
    options?: Partial<ConsolePageOption>
  ) {
    this.options = createDefaultConsolePageOption()
    if (options) {
      this.options = deepmerge(this.options, options)
    }
    const store = useConsolePageLayout()
    store.$patch(this.options)
    this.service = new DefaultConsolePageService()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  init(app: App<Element>): void { }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialized(app: App<Element>): void {
    const router = container.resolve<Router>(CORE_ROUTER)
    const routeExtend = container.resolve<RouteExtend>(CORE_ROUTER_EXTEND)
    const { template } = useMiscStore()
    let pageLayoutComponent = ConsolePageLayout
    if (template === 2) {
      pageLayoutComponent = SecondConsolePageLayout
    }
    router.addRoute({
      path: this.options.path,
      children: this.service.getChildRoute(),
      component: pageLayoutComponent,
      beforeEnter: [routeExtend.authRouter]
    })
  }
}
